/**
 * AURA Focus & Cursor Effects
 * Highlight hover/focus indicators and big cursor overrides.
 *
 * Hover outlines and custom cursors are gated behind media queries
 * so they only activate on devices with a hover-capable, fine pointer
 * (desktop / mouse-driven), avoiding "stuck hover" artefacts and
 * useless cursor rules on mobile touch devices.
 */

import { OrientationSettings } from '../../core/types';
import { cssInjector } from '../../core/css-injector';
import { Logger } from '../../core/logger';

const logger = new Logger('FocusCursor');

const STYLE_IDS = {
  highlightHover: 'highlight-hover',
  highlightFocus: 'highlight-focus',
  bigBlackCursor: 'big-black-cursor',
  bigWhiteCursor: 'big-white-cursor',
};

export function applyHighlightHover(enabled: boolean): void {
  if (enabled) {
    cssInjector.setStylesheet(
      STYLE_IDS.highlightHover,
      `
        @media (hover: hover) {
          :where(html) body *:hover {
            outline: 3px solid #4a90d9 !important;
            outline-offset: 2px !important;
          }
        }
      `,
      202
    );
    logger.debug('Highlight hover enabled');
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.highlightHover);
  }
}

export function applyHighlightFocus(enabled: boolean): void {
  if (enabled) {
    cssInjector.setStylesheet(
      STYLE_IDS.highlightFocus,
      `
        :where(html) body *:focus,
        :where(html) body *:focus-visible {
          outline: 4px solid #1e3a5f !important;
          outline-offset: 3px !important;
          box-shadow: 0 0 0 6px rgba(30, 58, 95, 0.3) !important;
        }
      `,
      203
    );
    logger.debug('Highlight focus enabled');
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.highlightFocus);
  }
}

export function applyCursors(settings: OrientationSettings): void {
  const wantBlack = settings.bigBlackCursor || (settings.bigCursors && !settings.bigWhiteCursor);
  const wantWhite = settings.bigWhiteCursor;

  if (wantBlack && !wantWhite) {
    setCursorStyle(STYLE_IDS.bigBlackCursor, '#000000');
    cssInjector.removeStylesheet(STYLE_IDS.bigWhiteCursor);
    logger.debug('Big black cursor enabled');
  } else if (wantWhite) {
    setCursorStyle(STYLE_IDS.bigWhiteCursor, '#ffffff', '#000000');
    cssInjector.removeStylesheet(STYLE_IDS.bigBlackCursor);
    logger.debug('Big white cursor enabled');
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.bigBlackCursor);
    cssInjector.removeStylesheet(STYLE_IDS.bigWhiteCursor);
  }
}

function setCursorStyle(styleId: string, fill: string, stroke?: string): void {
  const cursorUri = buildCursorDataUri(fill, stroke);
  cssInjector.setStylesheet(
    styleId,
    `
      @media (pointer: fine) {
        :where(html) body,
        :where(html) body * {
          cursor: url('${cursorUri}') 8 8, auto !important;
        }
      }
    `,
    204
  );
}

function buildCursorDataUri(fill: string, stroke = 'none'): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">` +
    `<path d="M8 8 L8 40 L16 32 L24 44 L28 42 L20 30 L32 30 Z" ` +
    `fill="${fill}" stroke="${stroke}" stroke-width="2"/>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
